import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { AIAssistant } from "../../components/ai/AIAssistant";
import {
  Home,
  LogOut,
  Plus,
  DollarSign,
  FileText,
  Users,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ChevronRight,
  Settings,
  Menu,
  X,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Building2,
  FileSearch
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Realtor Sidebar
function RealtorSidebar({ realtor, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  
  const navItems = [
    { icon: Home, label: "Dashboard", path: "/realtor/dashboard" },
    { icon: FileText, label: "Listings", path: "/realtor/listings" },
    { icon: Briefcase, label: "Transactions", path: "/realtor/transactions" },
    { icon: Users, label: "Buyers", path: "/realtor/buyers" },
    { icon: Building2, label: "Sellers", path: "/realtor/sellers" },
    { icon: FileSearch, label: "Title Services", path: "/realtor/title-services" },
    { icon: DollarSign, label: "Commission", path: "/realtor/commission" },
    { icon: TrendingUp, label: "Analytics", path: "/realtor/analytics" },
    { icon: Settings, label: "Settings", path: "/realtor/settings" },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-black border-r border-white/[0.06] transition-all z-40 ${
      collapsed ? "w-16" : "w-64"
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        {!collapsed && (
          <Link to="/realtor/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Home className="w-4 h-4 text-gold" />
            </div>
            <span className="font-heading font-bold text-white">REALTOR</span>
          </Link>
        )}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="text-white/50 hover:text-white p-1"
        >
          {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = window.location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 transition-all duration-300 ${
                isActive 
                  ? "bg-gold/10 text-gold border-l-2 border-gold" 
                  : "text-white/50 hover:text-white hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      {!collapsed && (
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full flex items-center justify-center">
              <span className="text-gold font-bold">
                {realtor?.name?.charAt(0) || "R"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{realtor?.name}</p>
              <p className="text-white/40 text-xs truncate">{realtor?.brokerage}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-[#FF3B30] py-2 border border-white/[0.08] hover:border-red-500/50 transition-all duration-300"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      )}
    </aside>
  );
}

// Stat Card
function StatCard({ label, value, subtext, icon: Icon, color = "white", link }) {
  const content = (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5 hover:border-gold/30 transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 flex items-center justify-center ${
          color === "gold" ? "bg-gold/10 border-gold/30" :
          color === "green" ? "bg-emerald-500/10 border-emerald-500/30" :
          color === "blue" ? "bg-blue-500/10 border-blue-500/30" :
          "bg-white/[0.08]/50 border-white/[0.1]"
        } border`}>
          <Icon className={`w-5 h-5 ${
            color === "gold" ? "text-gold" :
            color === "green" ? "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" :
            color === "blue" ? "text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" :
            "text-white/50"
          }`} />
        </div>
        {link && <ArrowUpRight className="w-4 h-4 text-white/40" />}
      </div>
      <div className={`font-mono text-2xl font-bold mb-1 ${
        color === "gold" ? "text-gold" : "text-white"
      }`}>
        {value}
      </div>
      <div className="text-white/50 text-sm">{label}</div>
      {subtext && <div className="text-white/40 text-xs mt-1">{subtext}</div>}
    </div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

export default function RealtorDashboardPage() {
  const navigate = useNavigate();
  const [realtor, setRealtor] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentListings, setRecentListings] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, listingsRes, transactionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/realtor/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/listings?status=active`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/realtor/transactions`, { withCredentials: true })
      ]);

      setRealtor(profileRes.data);
      setStats(statsRes.data);
      setRecentListings(listingsRes.data.slice(0, 5));
      setRecentTransactions(transactionsRes.data.slice(0, 5));
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/realtor");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/realtor/auth/logout`, {}, { withCredentials: true });
      navigate("/realtor");
    } catch (error) {
      navigate("/realtor");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <RealtorSidebar realtor={realtor} onLogout={handleLogout} />
      
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Dashboard</h1>
            <p className="text-white/50 mt-1">Welcome back, {realtor?.name?.split(' ')[0]}</p>
          </div>
          <Link to="/realtor/listings/new">
            <Button className="gold-gradient-btn rounded-none font-bold">
              <Plus className="w-5 h-5 mr-2" /> New Listing
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Active Listings"
            value={stats?.listings?.active || 0}
            icon={FileText}
            color="blue"
            link="/realtor/listings"
          />
          <StatCard
            label="Pending Transactions"
            value={stats?.transactions?.total_active || 0}
            icon={Clock}
            color="gold"
            link="/realtor/transactions"
          />
          <StatCard
            label="Pending Commission"
            value={formatCurrency(stats?.commission?.pending)}
            icon={DollarSign}
            color="green"
          />
          <StatCard
            label="Closed MTD"
            value={formatCurrency(stats?.commission?.closed_mtd)}
            icon={TrendingUp}
            color="gold"
          />
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Active Listings */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Active Listings</h2>
              <Link to="/realtor/listings" className="text-gold text-sm hover:underline">
                View All
              </Link>
            </div>
            
            {recentListings.length > 0 ? (
              <div className="space-y-3">
                {recentListings.map((listing) => (
                  <Link
                    key={listing.listing_id}
                    to={`/realtor/listings/${listing.listing_id}`}
                    className="flex items-center justify-between p-3 bg-black hover:border-gold/30 border border-transparent transition-all duration-300"
                  >
                    <div>
                      <p className="text-white font-medium">{listing.property_address}</p>
                      <p className="text-white/50 text-sm">
                        {listing.bedrooms}bd/{listing.bathrooms}ba • {listing.sqft?.toLocaleString()} sqft
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-gold font-mono font-bold">
                        {formatCurrency(listing.asking_price)}
                      </p>
                      <p className="text-white/40 text-xs">
                        {listing.offer_count || 0} offers
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-white/30 mx-auto mb-2" />
                <p className="text-white/50">No active listings</p>
                <Link to="/realtor/listings/new" className="text-gold text-sm hover:underline">
                  Add your first listing
                </Link>
              </div>
            )}
          </div>

          {/* Active Transactions */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white uppercase">Transactions</h2>
              <Link to="/realtor/transactions" className="text-gold text-sm hover:underline">
                View All
              </Link>
            </div>
            
            {recentTransactions.length > 0 ? (
              <div className="space-y-3">
                {recentTransactions.map((txn) => (
                  <Link
                    key={txn.transaction_id}
                    to={`/realtor/transactions/${txn.transaction_id}`}
                    className="flex items-center justify-between p-3 bg-black hover:border-gold/30 border border-transparent transition-all duration-300"
                  >
                    <div>
                      <p className="text-white font-medium">{txn.property_address}</p>
                      <p className="text-white/50 text-sm capitalize">
                        {txn.stage?.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-mono">
                        {formatCurrency(txn.sale_price)}
                      </p>
                      <p className="text-gold text-xs">
                        {formatCurrency(txn.estimated_commission)} comm
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Briefcase className="w-12 h-12 text-white/30 mx-auto mb-2" />
                <p className="text-white/50">No active transactions</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Offers Alert */}
        {stats?.offers?.pending > 0 && (
          <div className="mt-6 bg-amber-500/10 border border-amber-500/30 p-4 flex items-center gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-white font-medium">
                {stats.offers.pending} pending offer{stats.offers.pending > 1 ? 's' : ''} need review
              </p>
              <p className="text-white/50 text-sm">Review and respond to buyer offers</p>
            </div>
            <Link to="/realtor/listings">
              <Button variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/10 rounded-none">
                Review Offers
              </Button>
            </Link>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/realtor/listings/new" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/30 transition-all duration-300 text-center">
            <Plus className="w-8 h-8 text-gold mx-auto mb-2" />
            <p className="text-white text-sm">Add Listing</p>
          </Link>
          <Link to="/realtor/buyers" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/30 transition-all duration-300 text-center">
            <Users className="w-8 h-8 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] mx-auto mb-2" />
            <p className="text-white text-sm">Manage Buyers</p>
          </Link>
          <Link to="/realtor/transactions" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/30 transition-all duration-300 text-center">
            <Briefcase className="w-8 h-8 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] mx-auto mb-2" />
            <p className="text-white text-sm">Transactions</p>
          </Link>
          <Link to="/realtor/analytics" className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-gold/30 transition-all duration-300 text-center">
            <TrendingUp className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] mx-auto mb-2" />
            <p className="text-white text-sm">Analytics</p>
          </Link>
        </div>
      </main>

      {/* AI Assistant - Floating */}
      <AIAssistant userRole="realtor" variant="floating" />
    </div>
  );
}
